import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GqlService } from '@tgx/shared/data-access/graphql';
import { AdviseMessage } from '@tgx/shared/interfaces';
import { ToastsService } from '@tgx/shared/toasts';
import { lastValueFrom } from 'rxjs';
import { CertificationInput, hotelXMutations, hotelXQueries } from '../operations';
import { NotificationScope } from '@tgx/shared/enums';
import { NotificationsService, URL_WEB_BFF } from '@tgx/shared/services';
import { WEB_BFF_ENDPOINTS } from '@tgx/shared/constants';
import { SharedLoggingService } from '@tgx/shared/logging';

@Injectable({
  providedIn: 'root',
})
export class OnboardingImplementationService {
  private httpBackend = inject(HttpBackend);
  private gqlService = inject(GqlService);
  private toastService = inject(ToastsService);
  private notificationService = inject(NotificationsService);
  private loggingService = inject(SharedLoggingService);

  httpWithoutInterceptor: HttpClient;

  private readonly notificationEmail = 'client-onboarding@travelgate.com';

  private urlWebBff = inject(URL_WEB_BFF);

  constructor() {
    this.httpWithoutInterceptor = new HttpClient(this.httpBackend);
  }

  async uploadFile(file: File, orgCode: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadPath', `${orgCode}/Hotel-X`);

    try {
      const apiUrl = `${this.urlWebBff}${WEB_BFF_ENDPOINTS.UPLOAD_FTP_FILE}`;
      return await lastValueFrom(this.httpWithoutInterceptor.post(apiUrl, formData));
    } catch (error) {
      console.error('Upload failed', error);
      throw error;
    }
  }

  async checkCertificationStatus(orgCode: string): Promise<any> {
    try {
      const input: CertificationInput = {
        organizationCode: orgCode,
      };
      const gqlResponse: any = await this.gqlService.queryGateway(hotelXQueries.getCertificationStatus, {
        input: input,
      });

      if (gqlResponse?.hotelX?.certifications?.adviseMessage) {
        gqlResponse?.hotelX?.certifications?.adviseMessage.forEach((adv: AdviseMessage) => {
          this.toastService.addToastAdviseMessage(adv);
        });
        return false;
      }

      if (gqlResponse?.hotelX?.certifications?.edges) {
        const validations = gqlResponse?.hotelX?.certifications?.edges[0]?.node?.certificationData;
        return validations;
      }
    } catch (err: any) {
      this.toastService.handleGatewayAndGraphqlErrors(err);
      return false;
    }
  }

  async writeCertificationStatus(orgCode: string, fileName?: string): Promise<boolean> {
    try {
      const input: CertificationInput = {
        organizationCode: orgCode,
        ...(fileName ? { name: fileName } : {}),
      };
      const gqlResponse: any = await this.gqlService.mutationGateway(hotelXMutations.writeCertificationStatus, {
        input: input,
      });

      if (gqlResponse?.hotelX?.certification?.adviseMessage) {
        gqlResponse?.hotelX?.certification?.adviseMessage.forEach((adv: AdviseMessage) => {
          this.toastService.addToastAdviseMessage(adv);
        });
        return false;
      }

      if (gqlResponse?.hotelX?.certification?.edges) {
        const success = gqlResponse?.hotelX?.certification?.written;
        return success;
      }
      return false;
    } catch (err: any) {
      this.toastService.handleGatewayAndGraphqlErrors(err);
      return false;
    }
  }

  /**
   * Sends an email notification to 'client-onboarding@travelgate.com' with the buyer orgCode requesting for support
   */
  async sendSupportRequest(type: 'Buyer' | 'Seller', orgLabel: string, orgCode: string) {
    try {
      await this.notificationService.sendSimpleNotification(
        NotificationScope.USER,
        this.notificationEmail,
        `${type} \`${orgLabel} (${orgCode})\` in onboarding implementation process is requesting support.`,
        'https://app.travelgate.com/',
        true,
      );
    } catch (error) {
      this.loggingService.recordError('Error sending support request notification', {
        file: 'onboarding-implementation.service.ts',
        method: 'sendSupportRequest',
        description: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Sends an email notification to 'client-onboarding@travelgate.com' with the buyer orgCode
   */
  async sendEndNotification(orgLabel: string, orgCode: string) {
    try {
      await this.notificationService.sendSimpleNotification(
        NotificationScope.USER,
        this.notificationEmail,
        `Buyer \`${orgLabel} (${orgCode})\` has finished the certifications and onboarding implementation process!.`,
        'https://app.travelgate.com/',
        true,
      );
    } catch (error) {
      this.loggingService.recordError('Error sending end notification', {
        file: 'onboarding-implementation.service.ts',
        method: 'sendEndNotification',
        description: error instanceof Error ? error.message : String(error),
      });
    }
  }
}
